import type { AsyncReturnType } from "type-fest";
import type {
  TimingEvent,
  TimingSpan,
  TimingSpanWithEndTime,
  TimingSpanWithoutEndTime,
  TimingStrategy,
} from "../types";

type ConsoleLogTimingStrategyOptions = {
  logLevel: "debug" | "error" | "info" | "log" | "trace" | "warn";
};

const defaultConsoleLogTimingStrategyOptions: ConsoleLogTimingStrategyOptions = {
  logLevel: "debug",
};

/**
 * Strategy implementation for console log timing.
 */
export class ConsoleLogTimingStrategy implements TimingStrategy {
  private readonly options: ConsoleLogTimingStrategyOptions;

  public constructor(
    options?: ConsoleLogTimingStrategyOptions,
    private readonly consoleApi: typeof console = console,
    private readonly performanceApi: typeof performance = performance,
  ) {
    this.options = {
      ...defaultConsoleLogTimingStrategyOptions,
      ...options,
    };
  }

  public startSpan(span: TimingSpanWithoutEndTime): void {
    this.log(`(Started) ${span.name}`, span);
  }

  public endSpan(span: TimingSpanWithEndTime): void {
    this.log(`(Ended) ${span.name} [${span.startTime - span.endTime}ms]`, span);
  }

  public createSpan(span: TimingSpan): void {
    this.log(`${span.name} [${span.endTime ? span.endTime - span.startTime : -1}ms]`, span);
  }

  // biome-ignore lint/suspicious/noExplicitAny: Need to match ReturnType<F>, which uses any
  public async withSpan<F extends () => Promise<any>>(
    span: TimingSpanWithoutEndTime,
    callback: F,
  ): Promise<AsyncReturnType<F>> {
    const result = await callback();

    this.log(`${span.name} [${this.performanceApi.now() - span.startTime}ms]`, span);

    return result;
  }

  public createEvent(event: TimingEvent): void {
    this.log(event.name, event);
  }

  public reset(): void {
    // This space intentionally left blank
  }

  private log(message: string, spanOrEvent: TimingSpan | TimingEvent) {
    this.consoleApi[this.options.logLevel](`[Timing] ${message}`, spanOrEvent);
  }
}
